import jwtDecode from 'jwt-decode'

import actionTypes from 'redux/actions/actionTypes'
import makeUniqueId from 'helpers/makeUniqueId'
import breakpointStatus from 'helpers/enums/breakpointStatus'
import { openModal, closeModal } from 'redux/reducers/helpers/modals'
import { openConfirm, closeConfirm } from 'redux/reducers/helpers/confirms'
import confirmTypes from 'helpers/enums/confirmTypes'
import modalTypes from 'helpers/enums/modalTypes'
import errorCodes from 'helpers/enums/errorCodes'
import localErrorCodes from 'helpers/enums/errors/localErrorCodes'
import { videoStatusTypes } from 'helpers/interactiveVideoStatus'
import interactiveVideoStatusTypes from 'helpers/enums/interactiveVideoCreation/videoStatusTypes'
import editModes from 'helpers/enums/creationTool/editModes'

const defaultState = {
	uid: '',
	externalUid: '',
	presentationUid: '',
	settings: {
		form: {
			videoTitle: {
				error: '',
			},
		},
	},
	media: {
		updatedBreakpoint: '',
		asset: {
			// TODO: Make asset default with all it's properties
			// source: '',
			// uid: '',
			// type: '',
			status: videoStatusTypes.NONE,
			progress: 0,
		},
		breakpoints: [],
		credits: '',
		trimStart: 0,
		trimEnd: 1000,
	},
	currentActivity: {
		isEdition: false,
		breakpointUid: '',
		breakpointActivityUid: '',
	},
	modals: [],
	confirms: [],
	status: interactiveVideoStatusTypes.NONE,
	editMode: editModes.DUPLICATE_ON_EDIT,
	trimTool: {
		trimStart: 0,
		trimEnd: 1000,
		isActive: false,
		hasTrimmed: false,
	},
}

function initializeInteractiveVideo(state, action) {
	const decodedJwt = jwtDecode(action.jwt)
	return {
		...state,
		uid: action.uid,
		externalUid: decodedJwt.extra.custom_slide_id,
		presentationUid: decodedJwt.extra.presentation_id,
		editMode: decodedJwt.extra.shouldDuplicateInEdit === false ?
			editModes.NOT_DUPLICATE_ON_EDIT : editModes.DUPLICATE_ON_EDIT,
	}
}

function initializeActivityCreation(state, action) {
	const decodedJwt = jwtDecode(action.jwt)
	return {
		...state,
		uid: action.uid,
		externalUid: decodedJwt.extra.custom_slide_id,
		currentActivity: {
			...state.currentActivity,
			isEdition: action.isEdition,
			breakpointUid: action.breakpointUid,
			breakpointActivityUid: action.breakpointActivityUid,
		},
	}
}

function updateMediaData(state, action) {
	return {
		...state,
		media: {
			...state.media,
			uid: action.data.uid,
			duration: action.data.duration,
			asset: {
				...action.data.asset,
				status: videoStatusTypes.SUCCESS,
			},
			breakpoints: action.data.breakpoints.map(breakpoint => ({
				...breakpoint,
				clientId: makeUniqueId(),
				uid: breakpoint.uid,
				status: breakpointStatus.READY,
			})),
			posterUid: action.data.posterUid,
			credits: action.data.credits,
			trimStart: action.data.trimStart || 0,
			trimEnd: action.data.trimEnd || action.data.duration,
		},
		trimTool: {
			...state.trimTool,
			trimStart: action.data.trimStart || 0,
			trimEnd: action.data.trimEnd || action.data.duration,
		},
	}
}

function updateMediaCredits(state, action) {
	return {
		...state,
		media: {
			...state.media,
			credits: action.credits,
		},
	}
}

function updateMediaDataUnlink(state, action) {
	const newState = updateStatus(state, interactiveVideoStatusTypes.NONE)
	return updateMediaData(newState, action)
}


function addBreakpoint(state, action) {
	const newState = {
		...state,
		media: {
			...state.media,
			breakpoints: [
				...state.media.breakpoints,
				{
					clientId: action.breakpointClientId,
					status: breakpointStatus.SAVING,
					startTime: action.breakpointTime,
					endTime: action.breakpointTime + 1,
					activities: [],
				},
			],
		},
		modals: openModal(
			state.modals,
			modalTypes.INTERACTIVE_VIDEO_ACTIVITY_PICKER,
			{
				breakpointClientId: action.breakpointClientId,
				breakpointTime: action.breakpointTime,
			}
		),
	}
	return newState
}

function removeBreakpoint(state, action) {
	const newState = {
		...state,
		media: {
			...state.media,
			breakpoints: state.media.breakpoints.filter(
				breakpoint => breakpoint.clientId !== action.breakpointClientId
			),
		},
		modals: closeModal(
			state.modals,
			modalTypes.INTERACTIVE_VIDEO_ACTIVITY_PICKER,
		),
	}
	return newState
}

function updateBreakpoint(state, action) {
	return {
		...state,
		media: {
			...state.media,
			breakpoints: state.media.breakpoints.map(breakpoint => {
				if (breakpoint.clientId === action.breakpointClientId) {
					return {
						...breakpoint,
						uid: action.breakpointUid,
						status: breakpointStatus.READY,
					}
				} else {
					return breakpoint
				}
			}),
		},
	}
}

function updateMediaAsset(state, action) {
	return {
		...state,
		media: {
			...state.media,
			asset: {
				...state.media.asset,
				status: videoStatusTypes.PROCESSING,
				source: action.payload.source,
			},
		},
	}
}

function activityCreate(state) {
	return {
		...state,
		modals: closeModal(
			state.modals,
			modalTypes.INTERACTIVE_VIDEO_ACTIVITY_PICKER,
		),
	}
}

function initializeNewActivityCreation(state, action) {
	return {
		...state,
		currentActivity: {
			...state.currentActivity,
			breakpointActivityUid: action.breakpointActivityUid,
		},
	}
}

function openCancelConfirm(state, action) {
	let newState = {...state}

	if (action.isModified) {
		const confirmType = confirmTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_CANCEL_CONFIRM
		newState = {
			...state,
			confirms: openConfirm(state.confirms, confirmType),
		}
	}

	return newState
}

function openInteractiveVideoCancelConfirm(state) {

	if (state.media.asset.uid) {
		const confirmType = confirmTypes.INTERACTIVE_VIDEO_CREATION_CANCEL_CONFIRM
		state = {
			...state,
			confirms: openConfirm(state.confirms, confirmType),
		}
	}

	return state
}

function closeInteractiveVideoCancelConfirm(state) {
	const confirmType = confirmTypes.INTERACTIVE_VIDEO_CREATION_CANCEL_CONFIRM

	return {
		...state,
		confirms: closeConfirm(state.confirms, confirmType),
	}
}

function closeCancelConfirm(state) {
	const confirmType = confirmTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_CANCEL_CONFIRM

	return {
		...state,
		confirms: closeConfirm(state.confirms, confirmType),
	}
}

function setSettings(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			grades: action.payload.presentation.grades,
			subjects: action.payload.presentation.subjects,
			selectedGrades: action.payload.presentation.gradeIds,
			selectedSubject: action.payload.presentation.subjectId,
			videoTitle: action.payload.presentation.title,
			isUntitled: action.payload.presentation.isUntitled,
		},
	}
}

function updateSettings(state, action) {
	return {
		...state,
		settings: {
			...state.settings,
			selectedGrades: action.payload.gradesSelected,
			selectedSubject: action.payload.subjectSelected,
			videoTitle: action.payload.title,
			isUntitled: false,
		},

		modals: closeModal(
			state.modals,
			modalTypes.INTERACTIVE_VIDEO_SETTINGS,
		),
	}
}

function updateSettingsValidationErrors(state, action) {
	const validationErrors = [
		localErrorCodes.VALIDATION_ERROR,
		errorCodes.LESSON_DUPLICATED_NAME,
		errorCodes.LESSON_UNTITLED,
	]
	if (validationErrors.includes(action.error.error_code)) {
		return {
			...state,
			settings: {
				...state.settings,
				form: {
					videoTitle: {
						error: action.error.message,
					},
				},
			},
		}
	} else {
		return state
	}
}

function cleanSubmitErrors(state) {
	return {
		...state,
		settings: {
			...state.settings,
			form: {
				videoTitle: {
					error: '',
				},
			},
		},
	}
}

function openSettings(state) {
	return {
		...state,
		modals: openModal(
			state.modals,
			modalTypes.INTERACTIVE_VIDEO_SETTINGS,
		),
	}
}

function closeSettings(state) {
	return {
		...state,
		status: interactiveVideoStatusTypes.NONE,
		modals: closeModal(
			state.modals,
			modalTypes.INTERACTIVE_VIDEO_SETTINGS,
		),
	}
}

function openDeleteConfirm(state) {
	const confirmType = confirmTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE_CONFIRM

	return {
		...state,
		confirms: openConfirm(state.confirms, confirmType),
	}
}

function closeDeleteConfirm(state) {
	const confirmType = confirmTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE_CONFIRM

	return {
		...state,
		confirms: closeConfirm(state.confirms, confirmType),
	}
}

function deleteBreakpoint(state, action) {
	return {
		...state,
		media: {
			...state.media,
			breakpoints: state.media.breakpoints.filter(breakpoint => breakpoint.uid !== action.breakpointUid),
		},
		confirms: closeConfirm(
			state.modals,
			modalTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE_CONFIRM,
		),
	}
}

function clearCurrentActivity(state) {
	return {
		...state,
		currentActivity: defaultState.currentActivity,
	}
}

function setUpdatedBreakpoint(state) {
	return {
		...state,
		media: {
			...state.media,
			updatedBreakpoint: state.currentActivity.breakpointUid,
		},
	}
}

function updateBreakpointPosition(state, action) {
	return {
		...state,
		media: {
			...state.media,
			breakpoints: state.media.breakpoints.map(breakpoint => {
				if (breakpoint.uid === action.breakpointUid) {
					breakpoint.startTime = action.value
					breakpoint.endTime = action.value
				}
				return breakpoint
			}),
		},
	}
}

function setVideoStatus(state, action) {
	let videoStatus

	if (action.file.files[0].type === 'local') {
		videoStatus = videoStatusTypes.UPLOADING
	} else {
		videoStatus = videoStatusTypes.PROCESSING
	}

	return {
		...state,
		media: {
			...state.media,
			asset: {
				...state.media.asset,
				status: videoStatus,
				progress: 0,
			},
		},
	}
}

function finalize(state) {
	return {
		...state,
		media: {
			...state.media,
			updatedBreakpoint: '',
		},
	}
}

function unlinkAsset(state) {
	const newState = updateStatus(state, interactiveVideoStatusTypes.UNLINKING)
	return clearAsset(newState)
}

function clearAsset(state) {
	return {
		...state,
		media: {
			...state.media,
			asset: defaultState.media.asset,
		},
	}
}

function openSettingsForValidTitle(state, action) {
	if (action.error.error_code === errorCodes.DUPLICATED_LESSON_NAME ||
		action.error.error_code === errorCodes.UNTITLED_LESSON) {
		return openSettings(state)
	} else {
		return state
	}
}

function updateMediaAssetUploadProgress(state, action) {
	return {
		...state,
		media: {
			...state.media,
			asset: {
				...state.media.asset,
				progress: action.progress,
			},
		},
	}
}

function updateStatus(state, status) {
	return {
		...state,
		status,
	}
}

function updateTrimToolStart(state, action) {
	return {
		...state,
		trimTool: {
			...state.trimTool,
			trimStart: Math.min(action.time, state.trimTool.trimEnd - 1000),
		},
	}
}

function updateTrimToolEnd(state, action) {
	return {
		...state,
		trimTool: {
			...state.trimTool,
			trimEnd: Math.max(action.time, state.trimTool.trimStart + 1000),
		},
	}
}

function trimToolOpen(state) {
	return {
		...state,
		trimTool: {
			...state.trimTool,
			isActive: true,
		},
	}
}

function trimToolCancel(state) {
	return {
		...state,
		trimTool: {
			...state.trimTool,
			isActive: false,
			trimStart: state.media.trimStart,
			trimEnd: state.media.trimEnd,
		},
	}
}

function trimToolSaveTrim(state) {
	return {
		...state,
		media: {
			...state.media,
			trimStart: state.trimTool.trimStart,
			trimEnd: state.trimTool.trimEnd,
		},
		trimTool: {
			...state.trimTool,
			isActive: false,
			hasTrimmed: true,
		},
	}
}

const interactiveVideoReducer = (state = defaultState, action) => {
	switch (action.type) {
		case actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL:
		case actionTypes.INTERACTIVE_VIDEO_INITIALIZE_SELECT_ROUTE:
		case actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL_BASE:
		case actionTypes.INTERACTIVE_VIDEO_INITIALIZE_CREATION_TOOL_EDIT:
		case actionTypes.INTERACTIVE_VIDEO_INITIALIZE_SCREENSHOT_ROUTE:
		case actionTypes.VIDEO_INITIALIZE_EDIT_ROUTE:
		case actionTypes.VIDEO_INITIALIZE_CREATION_TOOL_BASE:
		case actionTypes.VIDEO_INITIALIZE_PICKER_ROUTE:
		case actionTypes.VIDEO_STANDALONE_INITIALIZE_PICKER_ROUTE:
		case actionTypes.VIDEO_STANDALONE_INITIALIZE_EDIT_ROUTE:
		case actionTypes.VIDEO_STANDALONE_INITIALIZE_BASE:
			return initializeInteractiveVideo(state, action)
		case actionTypes.INTERACTIVE_VIDEO_MEDIA_DATA_FETCHED:
			return updateMediaData(state, action)
		case actionTypes.INTERACTIVE_VIDEO_ASSET_VIDEO_UNLINK_SUCCESS:
			return updateMediaDataUnlink(state, action)
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_SUCCESS:
			return updateMediaAsset(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ADD_NEW_ACTIVITY:
			return addBreakpoint(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CANCEL_NEW_ACTIVITY:
			return removeBreakpoint(state, action)
		case actionTypes.INTERACTIVE_VIDEO_ACTIVITY_CREATE_SUCCESS:
			return updateBreakpoint(state, action)
		case actionTypes.INTERACTIVE_VIDEO_ACTIVITY_CREATE:
			return activityCreate(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_OEQ_INITIALIZE:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_INITIALIZE:
		case actionTypes.VIDEO_CREATION_MCQ_INITIALIZE:
		case actionTypes.VIDEO_CREATION_OEQ_INITIALIZE:
		case actionTypes.VIDEO_STANDALONE_MCQ_INITIALIZE:
		case actionTypes.VIDEO_STANDALONE_OEQ_INITIALIZE:
			return initializeActivityCreation(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_OEQ_CREATE_SUCCESS:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_CREATE_SUCCESS:
			return initializeNewActivityCreation(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_CANCEL:
			return openCancelConfirm(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_CANCEL:
			return openInteractiveVideoCancelConfirm(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_CANCEL_REJECTED:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_CANCEL_CONFIRMED:
			return closeCancelConfirm(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_CANCEL_REJECTED:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_CANCEL_CONFIRMED:
			return closeInteractiveVideoCancelConfirm(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_GET_SUCCESS:
			return setSettings(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_SAVE_SUCCESS:
			return updateSettings(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_SAVE_FAILED:
			return updateSettingsValidationErrors(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_SAVE:
			return cleanSubmitErrors(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_OPEN:
			return openSettings(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_SETTINGS_CLOSE:
			return closeSettings(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE:
			return openDeleteConfirm(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE_SUCCESS:
			return deleteBreakpoint(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_ACTIVITY_DELETE_CANCELED:
			return closeDeleteConfirm(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_OEQ_FINALIZE:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_FINALIZE:
		case actionTypes.VIDEO_STANDALONE_OEQ_FINALIZE:
		case actionTypes.VIDEO_STANDALONE_MCQ_FINALIZE:
			return clearCurrentActivity(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_OEQ_SAVE_SUCCESS:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_MCQ_SAVE_SUCCESS:
			return setUpdatedBreakpoint(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_BREAKPOINT_UPDATE_POSITION:
			return updateBreakpointPosition(state, action)
		case actionTypes.INTERACTIVE_VIDEO_FINALIZE_CREATION_TOOL:
			return finalize(state)
		case actionTypes.INTERACTIVE_VIDEO_GOTO_PICKER:
			return unlinkAsset(state, action)
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_PICKED:
			return setVideoStatus(state, action)
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_VIDEO_PROGRESS:
			return updateMediaAssetUploadProgress(state, action)
		case actionTypes.INTERACTIVE_VIDEO_PUBLISH_ERROR:
			return openSettingsForValidTitle(state, action)
		case actionTypes.INTERACTIVE_VIDEO_SAVE:
		case actionTypes.VIDEO_SAVE:
			return updateStatus(state, interactiveVideoStatusTypes.SAVING)
		case actionTypes.VIDEO_CANCEL_CONFIRMED:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_UNSAVED_BACK:
			return updateStatus(state, interactiveVideoStatusTypes.NONE)
		case actionTypes.INTERACTIVE_VIDEO_UPLOAD_CREDITS:
			return updateMediaCredits(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_ON_DRAG_TRIM_START:
			return updateTrimToolStart(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_ON_DRAG_TRIM_END:
			return updateTrimToolEnd(state, action)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_OPEN:
			return trimToolOpen(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_CANCEL:
			return trimToolCancel(state)
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_SAVE_TRIM:
		case actionTypes.INTERACTIVE_VIDEO_CREATION_TRIM_TOOL_UNSAVED_TRIM_AND_SAVE:
			return trimToolSaveTrim(state)
		default:
			return state
	}
}

export default interactiveVideoReducer

export {
	defaultState,
}
